import React, { useState } from 'react';
import logo from '../assets/gbl-logo.png';
import { useAuth } from '../context/AuthContext';
import { COUNTRIES, INTAKE_MONTHS, BANKS } from '../constants/onboarding';

const STEPS = [
  { label: 'Course Info' },
  { label: 'Loan Details' },
  { label: 'Banks' },
];

export default function OnboardingPage() {
  const { fbUser, student, completeOnboarding, skipOnboarding } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    course: '',
    university: '',
    country: '',
    intakeMonth: '',
    loanAmount: '',
    previousBanks: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const toggleBank = (bank) => {
    setForm((prev) => ({
      ...prev,
      previousBanks: prev.previousBanks.includes(bank)
        ? prev.previousBanks.filter((b) => b !== bank)
        : [...prev.previousBanks, bank],
    }));
  };

  const validateStep = () => {
    const errs = {};
    if (step === 0) {
      if (!form.course.trim()) errs.course = 'Course is required';
      if (!form.country) errs.country = 'Country is required';
      if (!form.intakeMonth) errs.intakeMonth = 'Intake month is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep((s) => s + 1);
  };

  const handleBack = () => setStep((s) => s - 1);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await completeOnboarding({
        course: form.course.trim(),
        university: form.university.trim(),
        country: form.country,
        intakeMonth: form.intakeMonth,
        loanAmount: form.loanAmount ? Number(form.loanAmount) : undefined,
        previousBanks: form.previousBanks.length > 0 ? form.previousBanks : undefined,
      });
    } catch (err) {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-start justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-lg animate-fade-in">
        <div className="text-center mb-6">
          <img src={logo} alt="GBL Tree" className="h-10 mx-auto mb-3" />
          <h1 className="text-xl font-normal text-[#202124]">Complete Your Profile</h1>
          <p className="text-sm text-[#5F6368] mt-1">Tell us about your course and loan requirements</p>
        </div>

        <div className="bg-white rounded-lg shadow-auth border border-[#DADCE0] overflow-hidden">
          <div className="flex justify-end px-6 pt-4">
            <button onClick={skipOnboarding} className="text-sm text-[#1A73E8] hover:text-[#1967D2] font-medium">
              Skip for now &rarr;
            </button>
          </div>

          {/* Step Indicator */}
          <div className="px-6 pt-2 pb-4">
            <div className="flex items-center justify-between">
              {STEPS.map((s, i) => (
                <React.Fragment key={i}>
                  <div className="flex flex-col items-center" style={{ flex: '0 0 auto' }}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      i < step ? 'bg-[#1E8E3E] text-white' : i === step ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#5F6368]'
                    }`}>
                      {i < step ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : i + 1}
                    </div>
                    <span className={`text-[10px] mt-1 font-medium ${i <= step ? 'text-[#202124]' : 'text-[#9AA0A6]'}`}>{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 mt-[-14px] rounded ${i < step ? 'bg-[#1E8E3E]' : 'bg-[#E8EAED]'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="px-6 pb-6">
            {step === 0 && (
              <div className="bg-[#F8F9FA] rounded-lg p-4 space-y-2 mb-5 border border-[#E8EAED]">
                <p className="text-xs font-medium text-[#5F6368] uppercase tracking-wider">Your Information</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <span className="text-xs text-[#9AA0A6]">Name</span>
                    <p className="text-sm font-medium text-[#202124]">{student?.name || fbUser?.displayName}</p>
                  </div>
                  <div>
                    <span className="text-xs text-[#9AA0A6]">Email</span>
                    <p className="text-sm font-medium text-[#202124] truncate">{fbUser?.email}</p>
                  </div>
                  {student?.phone && (
                    <div>
                      <span className="text-xs text-[#9AA0A6]">Phone</span>
                      <p className="text-sm font-medium text-[#202124]">{student.phone}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 0 && (
              <div className="space-y-4">
                <p className="text-sm font-medium text-[#202124]">Course Information</p>
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1.5">Course <span className="text-[#D93025]">*</span></label>
                  <input type="text" value={form.course} onChange={(e) => handleChange('course', e.target.value)} placeholder="e.g. MS Computer Science" className={`w-full input ${errors.course ? 'border-[#D93025]' : ''}`} />
                  {errors.course && <p className="text-xs text-[#D93025] mt-1">{errors.course}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1.5">University</label>
                  <input type="text" value={form.university} onChange={(e) => handleChange('university', e.target.value)} placeholder="e.g. University of Texas" className="w-full input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#5F6368] mb-1.5">Country <span className="text-[#D93025]">*</span></label>
                    <select value={form.country} onChange={(e) => handleChange('country', e.target.value)} className={`w-full input ${errors.country ? 'border-[#D93025]' : ''}`}>
                      <option value="">Select country</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.country && <p className="text-xs text-[#D93025] mt-1">{errors.country}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#5F6368] mb-1.5">Intake Month <span className="text-[#D93025]">*</span></label>
                    <select value={form.intakeMonth} onChange={(e) => handleChange('intakeMonth', e.target.value)} className={`w-full input ${errors.intakeMonth ? 'border-[#D93025]' : ''}`}>
                      <option value="">Select month</option>
                      {INTAKE_MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    {errors.intakeMonth && <p className="text-xs text-[#D93025] mt-1">{errors.intakeMonth}</p>}
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <p className="text-sm font-medium text-[#202124]">Loan Details</p>
                <p className="text-xs text-[#5F6368]">Provide your estimated loan requirement</p>
                <div>
                  <label className="block text-xs font-medium text-[#5F6368] mb-1.5">Loan Amount (in Lakhs)</label>
                  <input type="number" value={form.loanAmount} onChange={(e) => handleChange('loanAmount', e.target.value)} placeholder="e.g. 25" min="0" className="w-full input" />
                  <p className="text-[11px] text-[#9AA0A6] mt-1.5">Enter the approximate amount in Indian Lakhs</p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <p className="text-sm font-medium text-[#202124]">Previously Applied Banks</p>
                <p className="text-xs text-[#5F6368]">Select banks you've already applied to, if any</p>
                <div className="flex flex-wrap gap-2">
                  {BANKS.map((bank) => (
                    <button key={bank} type="button" onClick={() => toggleBank(bank)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                        form.previousBanks.includes(bank) ? 'bg-[#E8F0FE] border-[#1A73E8] text-[#1A73E8] font-medium' : 'bg-white border-[#DADCE0] text-[#5F6368] hover:border-[#5F6368]'
                      }`}>
                      {form.previousBanks.includes(bank) && (
                        <svg className="w-3 h-3 inline mr-1 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      )}
                      {bank}
                    </button>
                  ))}
                </div>
                {form.previousBanks.length > 0 && <p className="text-xs text-[#5F6368]">Selected: {form.previousBanks.join(', ')}</p>}
              </div>
            )}

            <div className="flex items-center gap-3 mt-6">
              {step > 0 && <button type="button" onClick={handleBack} className="flex-1 btn-secondary py-2.5 text-sm">Back</button>}
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={handleNext} className="flex-1 btn-primary py-2.5 text-sm">Next</button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={submitting} className="flex-1 btn-primary py-2.5 text-sm disabled:opacity-50">
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      Saving...
                    </span>
                  ) : 'Submit & Go to Dashboard'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
